import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    IconButton,
    makeStyles
} from '@material-ui/core';
import { Edit, ArrowBack, Add, Delete } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    titleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    section: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    addMemberSection: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}));

export default function CohortDetails({ open, onClose, cohort }) {
    const classes = useStyles();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});

    useEffect(() => {
        if (open && cohort) {
            fetchCohortMembers();
            setEditData({...cohort});
        }
    }, [open, cohort]);

    const fetchCohortMembers = async () => {
        if (!cohort) return;

        setLoading(true);
        try {
            const response = await fetch(`/api/cohort/${cohort.id}/members`);
            if (response.ok) {
                const membersData = await response.json();
                setMembers(membersData);
            }
        } catch (error) {
            console.error('Error fetching cohort members:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setEditData({...cohort});
    };

    const handleSave = async () => {
        try {
            const response = await fetch(`/api/cohort/${cohort.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editData),
            });

            if (response.ok) {
                const updatedCohort = await response.json();
                setEditing(false);
                onClose(); // Close the dialog
                window.location.reload(); // Refresh to see changes
            } else {
                alert('Failed to update cohort');
            }
        } catch (error) {
            console.error('Error updating cohort:', error);
            alert('Failed to update cohort');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddMember = async () => {
        if (!newMemberEmail.trim()) return;

        try {
            const response = await fetch(`/api/cohort/${cohort.id}/members`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: newMemberEmail.trim() }),
            });

            const data = await response.json();

            if (response.ok) {
                setNewMemberEmail('');
                fetchCohortMembers(); // Refresh the list
            } else {
                console.error('Server error:', data);
                alert(data.error || 'Failed to add member. Please check the email.');
            }
        } catch (error) {
            console.error('Error adding member:', error);
            alert('Failed to add member: ' + error.message);
        }
    };

    const handleRemoveMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to remove this member?')) {
            return;
        }

        try {
            const response = await fetch(`/api/cohort/${cohort.id}/members/${memberId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                fetchCohortMembers(); // Refresh the list
            } else {
                alert('Failed to remove member');
            }
        } catch (error) {
            console.error('Error removing member:', error);
            alert('Failed to remove member');
        }
    };

    if (!cohort) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Box className={classes.titleContainer}>
                    <span>
                        {editing ? 'Edit Cohort' : 'Cohort Details: ' + cohort.name}
                        {!editing && (
                            <IconButton
                                size="small"
                                onClick={handleEdit}
                                style={{ marginLeft: '10px' }}
                            >
                                <Edit />
                            </IconButton>
                        )}
                    </span>
                    <IconButton onClick={onClose}>
                        <ArrowBack />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {editing ? (
                    // Edit Form
                    <>
                        <TextField
                            margin="dense"
                            label="Cohort Name"
                            name="name"
                            value={editData.name || ''}
                            onChange={handleInputChange}
                            fullWidth
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Cohort Description"
                            name="description"
                            value={editData.description || ''}
                            onChange={handleInputChange}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <TextField
                            margin="dense"
                            label="Survey Link"
                            name="survey_link"
                            value={editData.survey_link || ''}
                            onChange={handleInputChange}
                            fullWidth
                            required
                        />
                    </>
                ) : (
                    // Display Mode
                    <>
                        <Box className={classes.section}>
                            <Typography variant="subtitle1">Description:</Typography>
                            <Typography variant="body2">
                                {cohort.description || 'No description provided'}
                            </Typography>
                        </Box>

                        <Box className={classes.section}>
                            <Typography variant="subtitle1">Survey Link:</Typography>
                            <Typography variant="body2">
                                <a href={cohort.survey_link} target="_blank" rel="noopener noreferrer">
                                    {cohort.survey_link}
                                </a>
                            </Typography>
                        </Box>
                    </>
                )}

                <Box className={classes.section}>
                    <Typography variant="h6">Members ({members.length})</Typography>

                    {/* Simple Add Member Form */}
                    <Box className={classes.addMemberSection}>
                        <TextField
                            label="Add member by email"
                            value={newMemberEmail}
                            onChange={(e) => setNewMemberEmail(e.target.value)}
                            placeholder="Enter email address"
                            size="small"
                            style={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddMember}
                            disabled={!newMemberEmail.trim()}
                            startIcon={<Add />}
                        >
                            Add
                        </Button>
                    </Box>

                    {loading ? (
                        <Typography>Loading members...</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Joined Date</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map(member => (
                                    <TableRow key={member.id}>
                                        <TableCell>{member.email}</TableCell>
                                        <TableCell>
                                            {member.date ? new Date(member.date).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveMember(member.id)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {members.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No members in this cohort
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                {editing ? (
                    <>
                        <Button onClick={handleCancelEdit} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSave} color="primary" variant="contained">
                            Save Changes
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose} color="primary">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}